import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import findIndex from "lodash/findIndex";
import sortBy from "lodash/sortBy";
import { PostModel } from "@dialogueconsulting/sked-preview";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";

import {
  AccountDropdown,
  accountTypesToSelectorAccounts,
  accountTypesToSelectorGroups,
} from "@/components/ui/selectors";
import { hideModal } from "actions/modal";
import { getPage } from "selectors/commonSelectors";
import { type PageName, PAGES } from "constants/Pages";
import { sortAccounts } from "utils/accounts";
import { useTrackEvent } from "libs/analytics";
import { SkedPostPreview } from "components/Posts/SkedPostPreview";
import type { AccountPlatformType, AccountType } from "shared/types/accounts";

type CollaboratingIgUser = {
  username: string;
  profilePictureUrl?: string;
};

type PreviewMediaFile = {
  isVideo?: boolean;
  thumbnailUrl?: string | null;
  type?: string;
  url?: string | null;
};

export type PreviewPost = {
  _id: string;
  accountId?: string;
  accountIds?: string[];
  caption?: string;
  collaboratingIgUsers?: CollaboratingIgUser[];
  mediaFiles?: PreviewMediaFile[];
  platform?: AccountPlatformType | "IG";
  redirectUrl?: string | null;
  schedule?: PreviewPost;
  thumbnailUrl?: string | null;
  timestamp?: string | number | Date;
  type?: string;
  url?: string | null;
} & Record<string, unknown>;

type PreviewPostModalProps = {
  post: PreviewPost;
  accounts: AccountType[];
  posts?: PreviewPost[] | null;
};

type PreviewAccount = Pick<
  AccountType,
  "_id" | "login" | "platformType" | "profilePictureUrl"
>;

type PreviewPostModel = NonNullable<
  React.ComponentProps<typeof SkedPostPreview>["post"]
> & {
  collaboratingIgUsers?: CollaboratingIgUser[];
  platform?: string;
  providedPost?: PreviewPost;
  type?: string;
};

type LegacyPreviewAccount = {
  id: string;
  profile_picture_url?: string;
  username: string;
};

const LegacyPostModel = PostModel as new (
  post: PreviewPost,
  account: LegacyPreviewAccount
) => PreviewPostModel;

type InstagramGridProps = {
  page?: PageName;
  post: PreviewPost;
  posts?: PreviewPost[] | null;
};

const PreviewPostModal = ({
  post,
  accounts,
  posts = null,
}: PreviewPostModalProps) => {
  const dispatch = useDispatch();
  const page = useSelector(getPage) as PageName | undefined;
  const trackEvent = useTrackEvent();

  const sortedAccounts = sortAccounts(accounts);
  const [selectedAccount, setSelectedAccount] = useState<
    AccountType | undefined
  >(sortedAccounts[0]);

  const selectorAccounts = useMemo(
    () => accountTypesToSelectorAccounts(sortedAccounts),
    [sortedAccounts]
  );
  const selectorGroups = useMemo(
    () => accountTypesToSelectorGroups(sortedAccounts),
    [sortedAccounts]
  );

  const postModel = useMemo(
    () => (selectedAccount ? getPostModel(post, selectedAccount, posts) : null),
    [post, posts, selectedAccount]
  );

  const handleTrackEvent = (eventName: string) => {
    if (page) {
      trackEvent({ eventName, metadata: { page: page.toLocaleLowerCase() } });
    }
  };

  if (!postModel) return null;

  const showInstagramGrid =
    postModel.platform === "IG" &&
    postModel.type !== "story" &&
    page === PAGES.UPCOMING;

  return (
    <Dialog open={true} onOpenChange={(open) => !open && dispatch(hideModal())}>
      <DialogContent
        className={`border-neutral-200 overflow-hidden ${
          showInstagramGrid ? "max-w-4xl" : "max-w-2xl"
        }`}
      >
        <DialogHeader>
          <DialogTitle className="font-gotham text-xl font-semibold text-neutral-900">
            Preview Post
          </DialogTitle>
        </DialogHeader>

        <div className="relative overflow-y-auto">
          <div className="flex gap-6 p-6">
            {showInstagramGrid && (
              <InstagramGrid post={post} posts={posts} page={page} />
            )}
            <div className="flex-1 min-w-[400px]">
              {sortedAccounts.length > 1 && (
                <div className="mb-4">
                  <AccountDropdown
                    accounts={selectorAccounts}
                    groups={selectorGroups}
                    value={
                      selectedAccount
                        ? { kind: "account", id: selectedAccount._id }
                        : undefined
                    }
                    onChange={(choice) => {
                      if (choice.kind !== "account") return;
                      const account = sortedAccounts.find(
                        (item) => item._id === choice.id
                      );
                      if (account) setSelectedAccount(account);
                    }}
                    aria-label="Preview account"
                  />
                </div>
              )}
              <SkedPostPreview
                post={postModel}
                trackEvent={handleTrackEvent}
                videoCoverPreview={
                  postModel.providedPost?.thumbnailUrl ?? undefined
                }
                env="schedugram-client"
              />
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const InstagramGrid = ({ page, post, posts }: InstagramGridProps) => {
  if (!posts || posts.length === 0 || page !== PAGES.UPCOMING) {
    return null;
  }

  const [postsForGrid, setPostsForGrid] = useState<PreviewPost[]>([]);
  const limit = 30;

  useEffect(() => {
    (async () => {
      let previewPosts: PreviewPost[] = [];

      //find index of preview post in posts array
      const postIndex = findIndex(posts, ["_id", post._id]);

      // Only look at posts that will be posted BEFORE the post index for preview.
      // We want to show the preview post at the top left and all posts posted up until then (scheduled or archived)
      // Only matching the account and not story as those don't show up in grid
      posts.forEach((currentPost, index) => {
        if (
          index > postIndex ||
          currentPost.accountId !== post.accountId ||
          currentPost.type === "story"
        ) {
          return;
        }

        previewPosts.push(currentPost);
      });
      // ensure we are sorting by timestamp, with the next to post at LAST in array (as this will be next to the archives posts)
      previewPosts = sortBy(previewPosts, "timestamp");
      previewPosts.reverse();

      // if we need more, then load more that are already posted, maybe we miss some upcoming if they have a bunch queued up?
      if (previewPosts.length < limit) {
        const resp = await axios.get<PreviewPost[]>(
          "/preview/archive/" + post.accountId,
          {
            params: { limit: limit - previewPosts.length },
          }
        );

        //check on post _id that we don't have any duplicates
        const additionalPosts = resp.data.filter((additionalPost) => {
          return previewPosts.every((previewPost) => {
            return previewPost._id !== additionalPost._id;
          });
        });

        previewPosts = previewPosts.concat(additionalPosts);
      }

      setPostsForGrid(previewPosts);
    })();
  }, []);

  return (
    <div
      className="mr-12 marvel-device iphone-x"
      style={{
        width: "310px",
        height: "600px",
      }}
    >
      <div className="top-bar"></div>
      <div className="sleep"></div>
      <div className="bottom-bar"></div>
      <div className="volume"></div>
      <div className="overflow">
        <div className="shadow shadow--tr"></div>
        <div className="shadow shadow--tl"></div>
        <div className="shadow shadow--br"></div>
        <div className="shadow shadow--bl"></div>
      </div>
      <div className="inner-shadow"></div>
      <div
        className="screen justify-center overflow-y-auto"
        style={{
          height: "101%",
          margin: "0 auto",
          padding: "3px",
          overflowX: "hidden",
          scrollbarWidth: "none",
        }}
      >
        <div
          className="   gap-1"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, max-content)",
            gridAutoRows: "max-content",
            justifyItems: "center",
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          {postsForGrid.map((gridPost) => (
            <div key={`ig-grid-${gridPost._id}`} className="w-100px h-100px">
              <img
                className="w-100px h-100px object-cover pr-1 pb-1"
                alt=""
                src={
                  gridPost.thumbnailUrl
                    ? gridPost.thumbnailUrl
                    : (gridPost.url ?? "")
                }
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const getPost = (
  post: PreviewPost,
  account: PreviewAccount,
  posts?: PreviewPost[] | null
): PreviewPost => {
  let _post = post;

  // multiple platforms in the drafts page
  if (
    _post.schedule &&
    Array.isArray(_post.accountIds) &&
    _post.accountIds.length > 1
  ) {
    const scheduledPost = posts?.find(
      (p) => p._id === post._id && p.accountIds?.includes(account._id)
    );
    _post = {
      ...(scheduledPost?.schedule ?? _post.schedule),
    };
  } else if (_post.schedule) {
    // drafts page
    _post = {
      ..._post.schedule,
    };
  }

  _post = {
    ..._post,
    platform: account.platformType || "IG",
  };

  return _post;
};

export const getPostModel = (
  post: PreviewPost,
  account: PreviewAccount,
  posts?: PreviewPost[] | null
): PreviewPostModel => {
  const collaboratingIgUsers = getCollaboratingIgUsers(account, post);

  return {
    ...new LegacyPostModel(getPost(post, account, posts), {
      id: account._id,
      profile_picture_url: account.profilePictureUrl,
      username: account.login,
    }),
    collaboratingIgUsers,
  } as PreviewPostModel;
};

export default PreviewPostModal;

function getCollaboratingIgUsers(
  account: PreviewAccount,
  post: PreviewPost
): CollaboratingIgUser[] {
  const currentUser = {
    username: account.login,
    profilePictureUrl: account.profilePictureUrl,
  };
  const collaboratingIgUsers: CollaboratingIgUser[] = [];
  if (post.collaboratingIgUsers?.length) {
    collaboratingIgUsers.push(currentUser);
    collaboratingIgUsers.push(...post.collaboratingIgUsers);
  }
  if (post.schedule?.collaboratingIgUsers?.length) {
    collaboratingIgUsers.push(currentUser);
    collaboratingIgUsers.push(...post.schedule.collaboratingIgUsers);
  }
  return collaboratingIgUsers;
}
